import { ZERO_WEI } from '@kwenta/sdk/constants'
import type { FuturesMarket, FuturesMarketAsset } from '@kwenta/sdk/types'
import {
	AssetDisplayByAsset,
	floorNumber,
	formatDollars,
	getDisplayAsset,
	getMarketName,
} from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import type { ColumnDef } from '@tanstack/react-table'
import { useRouter } from 'next/router'
import { memo, useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import FavoriteIcon from 'assets/svg/futures/favorite-star.svg'
import SelectedIcon from 'assets/svg/futures/selected-fav.svg'
import ColoredPrice from 'components/ColoredPrice'
import Currency from 'components/Currency'
import CurrencyIcon from 'components/Currency/CurrencyIcon'
import MarketBadge from 'components/MarketBadge'
import Spacer from 'components/Spacer'
import Table, { TableHeader, TableNoResults } from 'components/Table'
import Search from 'components/Table/Search'
import { Body, NumericValue } from 'components/Text'
import { FlexDivRowCentered } from 'components/layout/flex'
import { EVENT_NAME } from 'constants/matomoEvents'
import ROUTES from 'constants/routes'
import { useAnalytics } from 'hooks/useAnalytics'
import useWindowSize from 'hooks/useWindowSize'
import { setOpenModal } from 'state/app/reducer'
import {
	selectMarkPriceInfos,
	selectMarkets,
	selectPerpsProvider,
} from 'state/futures/common/selectors'
import { toggleFavoriteMarket } from 'state/futures/reducer'
import {
	selectFavoriteMarkets,
	selectMarketVolumes,
	selectMarketsQueryStatus,
} from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { select24hrPriceChanges } from 'state/prices/selectors'
import { FetchStatus } from 'state/types'
import media from 'styles/media'
import { getSynthDescription, providerIsCrossMargin } from 'utils/futures'

enum CategoryFilter {
	ALL = 'all',
	FAVORITES = 'favorites',
}

const FavoriteCell = memo(
	({
		asset,
		isFavorite,
		onSelectFav,
	}: {
		asset: FuturesMarketAsset
		isFavorite: boolean
		onSelectFav: (asset: FuturesMarketAsset) => void
	}) => (
		<div
			onClick={(e) => {
				onSelectFav(asset)
				e.stopPropagation()
			}}
			style={{ cursor: 'pointer', zIndex: 200 }}
		>
			{isFavorite ? (
				<SelectedIcon height={14} width={14} />
			) : (
				<FavoriteIcon height={14} width={14} />
			)}
		</div>
	)
)

const LabelCell = memo(({ asset }: { asset: FuturesMarketAsset }) => (
	<FlexDivRowCentered>
		<CurrencyIcon currencyKey={asset} width={18} height={18} />
		<Spacer width={10} />
		<Body size="medium">{getDisplayAsset(asset)}</Body>
	</FlexDivRowCentered>
))

const CATEGORIES = [
	{ label: 'futures.markets-drop-down.categories.all', filter: CategoryFilter.ALL },
	{ label: 'futures.markets-drop-down.categories.favorite', filter: CategoryFilter.FAVORITES },
]

const MarketsDropdownList = () => {
	const router = useRouter()
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const { trackTrade } = useAnalytics()
	const perpsProvider = useAppSelector(selectPerpsProvider)
	const markPrices = useAppSelector(selectMarkPriceInfos)
	const allMarkets = useAppSelector(selectMarkets) as FuturesMarket<string>[]
	const futuresVolumes = useAppSelector(selectMarketVolumes)
	const marketsQueryStatus = useAppSelector(selectMarketsQueryStatus)
	const favMarkets = useAppSelector(selectFavoriteMarkets)
	const priceChanges = useAppSelector(select24hrPriceChanges)
	const { deviceType } = useWindowSize()

	const containerRef = useRef<HTMLDivElement>(null)
	const [search, setSearch] = useState('')
	const [currentCategory, setCurrentCategory] = useState(CategoryFilter.ALL)
	const futuresMarkets = useMemo(() => {
		switch (currentCategory) {
			case CategoryFilter.ALL:
				return allMarkets
			case CategoryFilter.FAVORITES:
				return allMarkets.filter((m) => favMarkets.includes(m.asset))
			default:
				return allMarkets
		}
	}, [allMarkets, currentCategory, favMarkets])

	const getBasePriceRateInfo = useCallback(
		(asset: FuturesMarketAsset) => {
			return markPrices[asset]
		},
		[markPrices]
	)

	const options = useMemo(() => {
		const lowerSearch = search?.toLowerCase()
		const markets = lowerSearch
			? futuresMarkets.filter(
					(m) =>
						m.asset.toLowerCase().includes(lowerSearch) ||
						AssetDisplayByAsset[m.asset]?.toLocaleLowerCase().includes(lowerSearch)
				)
			: futuresMarkets

		const sortedMarkets = markets
			.filter((m) => favMarkets.includes(m.asset))
			.sort((a, b) =>
				wei(getBasePriceRateInfo(b.asset)?.price || 0)
					.sub(wei(getBasePriceRateInfo(a.asset)?.price || 0))
					.gt(0)
					? 1
					: -1
			)
			.concat(
				markets
					.filter((m) => !favMarkets.includes(m.asset))
					.sort((a, b) =>
						wei(getBasePriceRateInfo(b.asset)?.price || 0)
							.sub(wei(getBasePriceRateInfo(a.asset)?.price || 0))
							.gt(0)
							? 1
							: -1
					)
			)

		return sortedMarkets.map((market) => {
			const basePriceRate = getBasePriceRateInfo(market.asset)
			const marketAsset = providerIsCrossMargin(market.provider)
				? getDisplayAsset(market.asset) ?? market.asset
				: market.asset

			const change = priceChanges[market.asset] ?? 0
			return {
				value: market.asset,
				label: getMarketName(market.asset),
				asset: market.asset,
				description: getSynthDescription(market.asset, t),
				priceNum: Number(basePriceRate?.price || 0),
				price: formatDollars(basePriceRate?.price ?? '0', {
					suggestDecimalsForAsset: market.asset,
				}),
				change: change,
				priceDirection: basePriceRate?.change ?? null,
				isMarketClosed: market.isSuspended,
				closureReason: market.marketClosureReason,
				volume: futuresVolumes[marketAsset]?.volume ?? ZERO_WEI,
			}
		})
	}, [search, futuresMarkets, favMarkets, getBasePriceRateInfo, t, futuresVolumes, priceChanges])

	const onClearSearch = useCallback(() => setSearch(''), [])

	const onSelectMarket = useCallback(
		(asset: string) => {
			trackTrade(EVENT_NAME.SELECT_MARKET, asset)
			router.push(ROUTES.Markets.MarketPair(asset, perpsProvider))
			dispatch(setOpenModal(null))
		},
		[perpsProvider, dispatch, router, trackTrade]
	)

	const onSelectFav = useCallback(
		(asset: FuturesMarketAsset) => {
			dispatch(toggleFavoriteMarket({ asset }))
		},
		[dispatch]
	)

	const isFetching = !futuresMarkets.length && marketsQueryStatus?.status === FetchStatus.Loading

	const columns = useMemo<ColumnDef<(typeof options)[0]>[]>(() => {
		return [
			{
				header: () => (
					<TableHeader>
						<FavoriteIcon height={14} width={14} />
					</TableHeader>
				),
				accessorKey: 'favorite',
				sortingFn: 'basic',
				enableSorting: true,
				cell: ({ row }) => (
					<FavoriteCell
						asset={row.original.asset}
						isFavorite={favMarkets.includes(row.original.asset)}
						onSelectFav={onSelectFav}
					/>
				),
				size: 21,
			},
			{
				header: () => <TableHeader>{t('futures.markets-drop-down.market')}</TableHeader>,
				accessorKey: 'label',
				sortingFn: 'basic',
				enableSorting: true,
				cell: ({ row }) => <LabelCell asset={row.original.asset} />,
				size: 75,
			},
			{
				header: () => <TableHeader>{t('futures.markets-drop-down.price')}</TableHeader>,
				accessorKey: 'priceNum',
				sortingFn: 'basic',
				enableSorting: true,
				cell: (cellProps) => {
					return (
						<div>
							<ColoredPrice size="medium" priceChange={cellProps.row.original.priceDirection}>
								{cellProps.row.original.price}
							</ColoredPrice>
						</div>
					)
				},
				size: 70,
			},
			{
				header: () => (
					<StyledTableHeader>{t('futures.markets-drop-down.change')}</StyledTableHeader>
				),
				cell: ({ row }) => {
					return (
						<BadgeContainer>
							<MarketBadge
								fallbackComponent={
									<NumericValue
										size="medium"
										suffix="%"
										colored
										value={floorNumber(row.original.change ? row.original.change * 100 : '0', 2)}
									/>
								}
							/>
						</BadgeContainer>
					)
				},
				accessorKey: 'change',
				sortingFn: 'basic',
				enableSorting: true,
				size: 45,
			},
			{
				header: () => (
					<StyledTableHeader>{t('futures.markets-drop-down.volume')}</StyledTableHeader>
				),
				cell: ({ row }) => {
					return (
						<BadgeContainer>
							<MarketBadge
								fallbackComponent={
									<Currency.Price
										price={row.original.volume}
										formatOptions={{ truncateOver: 1e3, maxDecimals: 4 }}
									/>
								}
							/>
						</BadgeContainer>
					)
				},
				accessorKey: 'volume',
				sortingFn: (rowA, rowB) => {
					return wei(rowA.original.volume).cmp(rowB.original.volume)
				},
				enableSorting: true,
				size: 45,
			},
		]
	}, [favMarkets, t, onSelectFav])

	return (
		<MarketsList>
			<CategoryContainer>
				{CATEGORIES.map(({ label, filter }) => (
					<StyledBody
						key={label}
						size="medium"
						color={currentCategory === filter ? 'primary' : 'tertiary'}
						weight="bold"
						active={currentCategory === filter}
						onClick={() => setCurrentCategory(filter)}
					>
						{t(label)}
					</StyledBody>
				))}
			</CategoryContainer>
			<SearchBarContainer>
				<Search
					autoFocus
					onChange={setSearch}
					value={search}
					border={false}
					onClear={onClearSearch}
				/>
			</SearchBarContainer>
			<TableContainer ref={containerRef}>
				<StyledTable
					containerHeight={containerRef.current?.clientHeight}
					rounded={false}
					highlightRowsOnHover
					columnVisibility={{
						volume: deviceType !== 'mobile',
					}}
					onTableRowClick={(row) => onSelectMarket(row.original.asset)}
					columns={columns}
					data={options}
					columnsDeps={[favMarkets, options]}
					isLoading={isFetching}
					noBottom={true}
					noResultsMessage={
						options?.length === 0 ? (
							<TableNoResults>
								<Body color="secondary" size="large">
									{t('futures.markets-drop-down.no-results')}
								</Body>
							</TableNoResults>
						) : undefined
					}
					useVirtual
				/>
			</TableContainer>
		</MarketsList>
	)
}

const StyledBody = styled(Body)<{ active: boolean }>`
	padding: 14px 0px;
	cursor: pointer;
	${(props) =>
		props.active &&
		`
		padding-bottom: 12px;
		border-bottom: 2px solid #fff;
	`}
`
const CategoryContainer = styled(FlexDivRowCentered)`
	padding: 0px 20px;
	border-bottom: 1px solid #21201f;
	gap: 20px;
	justify-content: flex-start;
`

const BadgeContainer = styled(FlexDivRowCentered)`
	text-align: right;
	width: 60px;
	justify-content: flex-end;
`

const SearchBarContainer = styled.div`
	font-size: 13px;
	width: 100%;
	height: 38px;
	top: 0;
`

const MarketsList = styled.div`
	width: 422px;
	z-index: 100;
	box-shadow: 0px 8px 20px 0px rgba(0, 0, 0, 0.4);
	border: 1px solid #21201f;
	border-radius: 6px;

	background-color: ${(props) =>
		props.theme.colors.selectedTheme.newTheme.containers.primary.background};

	${media.lessThan('md')`
		width: 100vw;
	`}
`

const TableContainer = styled.div`
	height: 100%;
	max-height: 522px;
	overflow: scroll;
	border-top: ${(props) => props.theme.colors.selectedTheme.border};
	${media.lessThan('md')`
		height: calc(100vh - 70px);
		max-height: 100%;
	`}
`

const StyledTable = styled(Table)<{ mobile?: boolean }>`
	border: none;
	cursor: pointer;
	.table-body-row {
		padding: 0;
	}

	.table-body-cell {
		height: 32px;
	}
` as typeof Table

const StyledTableHeader = styled(TableHeader)`
	width: 70px;
	text-align: right;
`

export default MarketsDropdownList
